import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Circle, Share2, AlertTriangle, ChevronDown, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { getSession, getSummary, updatePayment } from "@/lib/api";
import { fmt } from "@/lib/calc";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";

const methodLabel = (m) => ({ cash: "Cash", revolut: "Revolut", card: "Card" }[m] || "—");

export default function HostDashboard() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [session, setSession] = useState(null);

  const load = useCallback(() => {
    Promise.all([getSummary(code), getSession(code)])
      .then(([sum, s]) => { setSummary(sum); setSession(s); })
      .catch(() => {});
  }, [code]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const togglePaid = async (g) => {
    try {
      const s = await updatePayment(code, g.guest_id, !g.paid, g.payment_method);
      setSession(s);
      const sum = await getSummary(code);
      setSummary(sum);
    } catch {
      toast.error("Eroare");
    }
  };

  if (!summary) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  const cur = summary.currency;
  const guests = summary.guests;
  const paidTotal = guests.filter((g) => g.paid).reduce((a, g) => a + g.total, 0);
  const allTotal = guests.reduce((a, g) => a + g.total, 0);
  const matches = Math.abs(summary.computed_total - (allTotal + summary.unassigned)) < 0.05;

  return (
    <div className="min-h-screen bg-background pb-16">
      <Header subtitle="Panou de control" />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tight">{session?.restaurant_name || "Nota ta"}</h1>
            <p className="text-muted-foreground mt-1">{guests.length} persoane · cod <span className="font-mono">{code}</span></p>
          </div>
          <button
            data-testid="dashboard-share-button"
            onClick={() => navigate(`/session/${code}/share`)}
            className="w-11 h-11 rounded-full bg-secondary flex items-center justify-center active:scale-90 transition"
          >
            <Share2 size={18} />
          </button>
        </div>

        {/* Overview */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="bg-card border border-border rounded-[24px] p-5">
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Total notă</span>
            <p data-testid="dashboard-total" className="font-display text-3xl font-black tracking-tight mt-1">{fmt(summary.computed_total, cur)}</p>
          </div>
          <div className="bg-card border border-border rounded-[24px] p-5">
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Încasat</span>
            <p data-testid="dashboard-collected" className="font-display text-3xl font-black tracking-tight mt-1 text-success">{fmt(paidTotal, cur)}</p>
          </div>
        </div>

        <button
          data-testid="host-select-items-button"
          onClick={() => navigate(`/session/${code}/select`)}
          className="mt-4 w-full bg-primary text-primary-foreground rounded-full py-4 px-6 font-bold active:scale-[0.98] transition-transform flex justify-center items-center gap-2"
        >
          <UtensilsCrossed size={18} /> Alege ce ai consumat tu
        </button>

        {summary.unassigned > 0.01 && (
          <div className="mt-3 bg-destructive/10 text-destructive rounded-[20px] p-4 flex items-center gap-3 text-sm font-medium" data-testid="unassigned-warning">
            <AlertTriangle size={18} />
            {fmt(summary.unassigned, cur)} încă nealese de nimeni
          </div>
        )}

        {/* Guests */}
        <div className="mt-6 space-y-3" data-testid="dashboard-guests">
          {guests.length === 0 && <p className="text-muted-foreground text-center py-8">Încă nu s-a alăturat nimeni.</p>}
          {guests.map((g, i) => (
            <motion.div
              key={g.guest_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-card border border-border rounded-[24px] overflow-hidden"
              data-testid={`dashboard-guest-${i}`}
            >
              <Collapsible>
                <div className="p-4 flex items-center gap-3">
                  <span className="w-11 h-11 shrink-0 rounded-full bg-secondary flex items-center justify-center font-bold">
                    {g.name.charAt(0).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{g.name}{g.guest_id === session?.host_id && <span className="text-muted-foreground font-normal"> (tu)</span>}</p>
                      {g.paid && <span className="text-[10px] uppercase font-bold text-success bg-success/10 rounded-full px-2 py-0.5">{methodLabel(g.payment_method)}</span>}
                    </div>
                    <CollapsibleTrigger asChild>
                      <button className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`expand-guest-${i}`}>
                        {g.items.length} produse <ChevronDown size={12} />
                      </button>
                    </CollapsibleTrigger>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-xl font-black tracking-tight">{fmt(g.total, cur)}</p>
                  </div>
                  <button
                    data-testid={`toggle-paid-${i}`}
                    onClick={() => togglePaid(g)}
                    className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition active:scale-90 ${
                      g.paid ? "text-success" : "text-muted-foreground"
                    }`}
                    aria-label="Marcat plătit"
                  >
                    {g.paid ? <CheckCircle2 size={26} /> : <Circle size={26} />}
                  </button>
                </div>
                <CollapsibleContent>
                  <div className="px-4 pb-4 pt-1 border-t border-border space-y-2">
                    {g.items.length === 0 && <p className="text-sm text-muted-foreground pt-2">Nu a ales nimic încă.</p>}
                    {g.items.map((it) => (
                      <div key={it.item_id} className="flex items-center justify-between text-sm pt-2">
                        <span className="text-muted-foreground">
                          {it.name}
                          {it.total_units > 1 && <span className="text-muted-foreground"> · {it.units_count}/{it.total_units} porții</span>}
                          {it.shared && <span className="text-interactive"> · împărțit</span>}
                        </span>
                        <span className="font-mono">{fmt(it.portion, cur)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                      <span className="text-muted-foreground">Taxe & bacșiș (proporțional)</span>
                      <span className="font-mono">{fmt(g.adjustments, cur)}</span>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </motion.div>
          ))}
        </div>

        <div className={`mt-6 rounded-[20px] p-4 text-sm font-medium flex items-center gap-3 ${matches ? "bg-success/10 text-success" : "bg-secondary text-muted-foreground"}`} data-testid="reconcile-status">
          <CheckCircle2 size={18} />
          {matches ? "Suma alocată corespunde cu totalul bonului." : "Sumele se actualizează pe măsură ce invitații aleg produse."}
        </div>
      </main>
    </div>
  );
}
